/**
 * HowItWorksPage - Canonical Platform Explainer
 * 
 * Single source of truth for how UnityLove functions.
 * Referenced by footer, onboarding, and learning surfaces.
 * 
 * IMPORTANT: Do not duplicate this copy elsewhere.
 * Other UI elements should link to /how-it-works or reference specific sections.
 */

import React from 'react';
import { ArrowLeft, Zap, Heart, Calendar, Lock, Shield, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GlobalNav } from '../components/GlobalNav';
import GlobalFooter from '../components/GlobalFooter';

export default function HowItWorksPage() {
  const navigate = useNavigate();

  const Section = ({ id, icon: Icon, title, children }) => (
    <section id={id} className="scroll-mt-24 pb-8 border-b border-gray-200 dark:border-slate-800 last:border-0">
      <div className="flex items-center gap-3 mb-4">
        {Icon && <Icon className="w-5 h-5 text-rose-400 flex-shrink-0" />}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      <div className="text-gray-600 dark:text-gray-300 space-y-4 leading-relaxed">
        {children}
      </div>
    </section>
  );

  const SubSection = ({ title, children }) => (
    <div className="mt-4">
      <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>
      <div className="text-gray-600 dark:text-gray-300 space-y-2">
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <GlobalNav />
      
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm font-medium text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>

        {/* Page Header */}
        <header className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Learn How Unity Love Works
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Unity Love is built differently on purpose. This section explains <em>how</em> and <em>why</em> — so you can move through the platform with confidence.
          </p>
        </header>

        {/* Content Sections */}
        <div className="space-y-10">
          
          {/* 1. How Unity Love Works */}
          <Section id="how-it-works" icon={Zap} title="1️⃣ How Unity Love Works">
            <p>
              Unity Love isn&apos;t about endless swiping or chasing attention. It&apos;s designed around <strong>real interaction</strong>, <strong>intentional pacing</strong>, and <strong>clear boundaries</strong>.
            </p>

            <SubSection title="The Core Areas">
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Events</p>
                  <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                    <li>Daily text and video speed dating</li>
                    <li>Designed for focused, real-time connection</li>
                    <li>Some events require tickets and verification</li>
                    <li>Event time is <strong>separate</strong> from your monthly minutes</li>
                  </ul>
                </div>
                
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Lounge</p>
                  <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                    <li>Open conversations and group interaction</li>
                    <li>Daily time limits keep things healthy and human</li>
                    <li>Upgrades extend access, not control outcomes</li>
                  </ul>
                </div>
                
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Inbox</p>
                  <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                    <li>One-on-one conversations after connection</li>
                    <li>No spam, no random DMs without context</li>
                  </ul>
                </div>
              </div>
            </SubSection>

            <SubSection title="Why Time Limits Exist">
              <p>
                Time limits aren&apos;t about restriction — they&apos;re about quality. They prevent burnout, reduce bad behavior, and keep rooms intentional.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                Upgrades increase access — they never bypass trust.
              </p>
            </SubSection>
          </Section>

          {/* 2. Relationship Styles */}
          <Section id="relationship-styles" icon={Heart} title="2️⃣ Relationship Styles on Unity Love">
            <p>
              Unity Love supports different relationship structures without hierarchy or judgment.
            </p>

            <SubSection title="Supported Styles">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><strong>Monogamous</strong></li>
                <li><strong>Polyamorous</strong></li>
                <li><strong>Polygamous</strong></li>
                <li><strong>Open Relationship</strong></li>
                <li><strong>Solo Poly</strong></li>
                <li><strong>Couples</strong></li>
                <li><strong>Singles open to couples</strong></li>
              </ul>
              
              <p className="mt-3">Each profile clearly shows:</p>
              <ul className="list-disc list-inside ml-2 space-y-1 text-sm">
                <li>How someone identifies</li>
                <li>Who they&apos;re open to connecting with</li>
                <li>What kind of interaction they&apos;re seeking</li>
              </ul>
            </SubSection>

            <SubSection title="Shared Expectations">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Clarity beats assumptions</li>
                <li>Consent is continuous</li>
                <li>Respect matters more here than on swipe apps</li>
              </ul>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                Unity Love works best when people are honest about what they want — and what they don&apos;t.
              </p>
            </SubSection>
          </Section>

          {/* 3. Events & Speed Dating */}
          <Section id="events" icon={Calendar} title="3️⃣ Events & Speed Dating Explained">
            <p>Events are the heartbeat of Unity Love.</p>

            <SubSection title="Event Types">
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Text Speed Dating</p>
                  <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                    <li>Available throughout the day</li>
                    <li>Low pressure, conversation-first</li>
                  </ul>
                </div>
                
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">Video Speed Dating</p>
                  <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                    <li>Hosted daily by region</li>
                    <li>Real faces, real presence</li>
                    <li>Stronger matching signals</li>
                  </ul>
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-3">
                This structure allows people to connect in their time zone without overcrowding events.
              </p>
            </SubSection>

            <SubSection title="Ticketed Events">
              <p>Some events include a ticket:</p>
              <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                <li>Each ticket includes <strong>up to 60 minutes</strong></li>
                <li>Ticketed minutes do <strong>not</strong> affect your plan limits</li>
                <li>Verification is required</li>
              </ul>
            </SubSection>

            <SubSection title="VIP Tickets">
              <p>VIP improves <em>placement</em>, not time:</p>
              <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-sm">
                <li>Priority entry</li>
                <li>Early matching rounds</li>
                <li>Visibility boost</li>
              </ul>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                VIP never guarantees matches — it improves opportunity.
              </p>
            </SubSection>
          </Section>

          {/* 4. Private Media & Trust */}
          <Section id="private-media" icon={Lock} title="4️⃣ Private Media & Trust">
            <p>Private photos and videos are about <strong>consent</strong>, not curiosity.</p>

            <SubSection title="How Private Media Works">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>You choose what&apos;s public and what&apos;s private</li>
                <li>Private media is locked by default</li>
                <li>It unlocks only through <strong>mutual connection and consent</strong></li>
              </ul>
            </SubSection>

            <SubSection title="Why Unity Love Does This">
              <p>
                Trust shouldn&apos;t be rushed. Private access is earned through interaction — not clicks.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                This protects everyone, especially in non-traditional relationship spaces.
              </p>
            </SubSection>
          </Section>

          {/* 5. Safety, Verification & Real People */}
          <Section id="safety" icon={Shield} title="5️⃣ Safety, Verification & Real People">
            <p>Unity Love is built for people who value authenticity.</p>

            <SubSection title="Verification">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Confirms age and identity</li>
                <li>Required for certain events</li>
                <li>Never shared publicly</li>
              </ul>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                Verification doesn&apos;t make someone &quot;better&quot; — it makes the space safer.
              </p>
            </SubSection>

            <SubSection title="What Unity Love Does Not Do">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>No shadow profiles</li>
                <li>No engagement bait</li>
                <li>No algorithmic manipulation of attraction</li>
              </ul>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                You see people because they chose to be here — not because an algorithm decided.
              </p>
            </SubSection>
          </Section>

          {/* 6. Love Links & Community */}
          <Section id="lovelinks" icon={Link2} title="6️⃣ Love Links & Community (Optional)">
            <p>Love Links is Unity Love&apos;s referral system.</p>

            <SubSection title="How It Works">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>Share your link</li>
                <li>Earn from people you genuinely invite</li>
                <li>Rewards are recurring, transparent, and optional</li>
              </ul>
            </SubSection>

            <SubSection title="Important Notes">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>No pressure</li>
                <li>No spam tactics</li>
                <li>No MLM nonsense</li>
              </ul>
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">
                It exists for people who want to build with the platform — not for everyone.
              </p>
            </SubSection>
          </Section>

          {/* Closing */}
          <div className="pt-8 text-center">
            <p className="text-gray-600 dark:text-gray-300 italic">
              Unity Love is built to be explored, not rushed.
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              Take your time. The right connections do too.
            </p>
          </div>

        </div>

        {/* Bottom Spacing */}
        <div className="h-16" />
      </main>

      <GlobalFooter />
    </div>
  );
}
